/**
 * ChatSessionSidebar Component
 *
 * Displays a list of chat sessions in the sidebar with actions for:
 * - Creating new sessions
 * - Selecting sessions
 * - Renaming sessions (inline edit)
 * - Deleting sessions (row "..." menu, then a confirmation dialog)
 * - Reading other users' chats in the read-only "Team chats" section
 *
 * Integrates with the chatStore for state management.
 */

import { useEffect, useMemo, useReducer, useState, useRef, useCallback, memo, type KeyboardEvent } from 'react';
import { useChatStore } from '@/stores/chatStore';
import { api, type ChatSession } from '@/services/api';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { RowActionsMenu } from '@/components/ui/row-actions-menu';
import {
  INITIAL_CHAT_DELETE_STATE,
  chatDeleteConfirmationMessage,
  chatDeleteReducer,
  chatRowActionItems,
} from '@/components/chat/chatDeleteFlow';
import { PinToggle } from '@/components/PinToggle';
import { TeamChatsSection } from '@/components/chat/TeamChatsSection';
import {
  groupTeamChats,
  ownChats,
  readTeamChatCollapseState,
  toggleCollapsedGroup,
  writeTeamChatCollapseState,
  type TeamChatCollapseState,
} from '@/components/chat/teamChats';
import { formatServerTimestamp } from '@/lib/timestamps';
import { cn } from '@/lib/utils';
import { useSettingsStore } from '@/stores/settingsStore';
import {
  MessageSquare,
  Plus,
  ChevronDown,
  Loader2,
  AlertCircle,
  Check,
  X,
  RefreshCw,
} from 'lucide-react';

// ============================================
// Types
// ============================================

interface ChatSessionSidebarProps {
  /** Whether the sidebar is collapsed */
  isCollapsed?: boolean;
  /** Callback when a session is selected */
  onSessionSelect?: (sessionId: string) => void;
}

interface SessionItemProps {
  session: ChatSession;
  isActive: boolean;
  onSelect: () => void;
  onRename: (name: string) => Promise<boolean>;
  onRequestDelete: (session: ChatSession) => void;
}

// ============================================
// Row Actions Component
// ============================================

/**
 * The "..." menu on a chat row. Events stop here so that opening the menu
 * never selects or renames the row.
 */
export function ChatRowActions({
  session,
  onRequestDelete,
}: {
  session: ChatSession;
  onRequestDelete: (session: ChatSession) => void;
}) {
  const items = chatRowActionItems(session, () => onRequestDelete(session));
  if (items.length === 0) return null;
  return (
    <span
      className="shrink-0"
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <RowActionsMenu items={items} label={`Actions for ${session.name}`} />
    </span>
  );
}

/** A delete failure, shown in the sidebar until dismissed. */
export function ChatDeleteError({ error, onDismiss }: { error: string; onDismiss: () => void }) {
  return (
    <div
      role="alert"
      className="mx-2 mb-2 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300"
    >
      <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <span className="min-w-0 flex-1 break-words">Could not delete the chat: {error}</span>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss error"
        className="shrink-0 rounded text-red-600 hover:text-red-800 dark:text-red-300 dark:hover:text-red-100"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

// ============================================
// Session Item Component
// ============================================

const SessionItem = memo(function SessionItem({
  session,
  isActive,
  onSelect,
  onRename,
  onRequestDelete,
}: SessionItemProps) {
  const timeZone = useSettingsStore((state) => state.contextTimezone);
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(session.name);
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Handle rename submission
  const handleRenameSubmit = useCallback(async () => {
    const trimmedName = editName.trim();
    if (!trimmedName || trimmedName === session.name) {
      setEditName(session.name);
      setIsEditing(false);
      return;
    }

    const success = await onRename(trimmedName);
    if (success) {
      setIsEditing(false);
    } else {
      // Revert on failure
      setEditName(session.name);
    }
  }, [editName, session.name, onRename]);

  // Handle rename cancel
  const handleRenameCancel = useCallback(() => {
    setEditName(session.name);
    setIsEditing(false);
  }, [session.name]);

  // Handle rename keyboard events
  const handleRenameKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleRenameSubmit();
      } else if (e.key === 'Escape') {
        handleRenameCancel();
      }
    },
    [handleRenameSubmit, handleRenameCancel]
  );

  // Handle double click to rename (must be before early returns)
  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsEditing(true);
  }, []);

  // Format date for display
  const formattedDate = formatServerTimestamp(session.updatedAt, timeZone, {
    month: 'short',
    day: 'numeric',
  });

  // Editing mode
  if (isEditing) {
    return (
      <div
        className={cn(
          'flex items-center gap-2 px-3 py-2 mx-2 rounded-lg',
          'bg-surface-100 dark:bg-surface-800'
        )}
      >
        <input
          ref={inputRef}
          type="text"
          value={editName}
          onChange={(e) => setEditName(e.target.value)}
          onKeyDown={handleRenameKeyDown}
          onBlur={handleRenameSubmit}
          className={cn(
            'flex-1 bg-white dark:bg-surface-900',
            'border border-surface-300 dark:border-surface-600',
            'rounded px-2 py-1 text-sm',
            'text-surface-900 dark:text-surface-100',
            'focus:outline-none focus:ring-2 focus:ring-primary-500/50'
          )}
        />
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0"
          onClick={handleRenameSubmit}
          title="Save"
        >
          <Check className="h-4 w-4 text-green-600" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0"
          onClick={handleRenameCancel}
          title="Cancel"
        >
          <X className="h-4 w-4 text-surface-500" />
        </Button>
      </div>
    );
  }

  // Normal session item
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onDoubleClick={handleDoubleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
      className={cn(
        'group w-full flex items-center gap-2 px-3 py-2 mx-2 rounded-lg text-left cursor-pointer',
        'transition-colors',
        isActive
          ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
          : 'text-surface-700 dark:text-surface-300 hover:bg-surface-100 dark:hover:bg-surface-800'
      )}
      style={{ width: 'calc(100% - 16px)' }}
    >
      <MessageSquare className="h-4 w-4 shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{session.name}</p>
        <p className="text-xs text-surface-500 dark:text-surface-400 truncate">
          {(session.provider === 'codex' ? 'Codex' : 'Claude')} • {session.messageCount} {session.messageCount === 1 ? 'message' : 'messages'} • {formattedDate}
        </p>
      </div>
      <PinToggle kind="chat" entityId={session.id} className="h-7 w-7" />
      <ChatRowActions session={session} onRequestDelete={onRequestDelete} />
    </div>
  );
});

// ============================================
// Loading State Component
// ============================================

function LoadingState() {
  return (
    <div className="flex-1 flex items-center justify-center p-4">
      <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
    </div>
  );
}

// ============================================
// Error State Component
// ============================================

function ErrorState({ error, onRetry }: { error: string; onRetry: () => void }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center p-4 text-center">
      <div className="w-12 h-12 rounded-full bg-destructive-100 dark:bg-destructive-900/20 flex items-center justify-center mb-3">
        <AlertCircle className="h-6 w-6 text-destructive-600 dark:text-destructive-400" />
      </div>
      <p className="text-sm text-destructive-600 dark:text-destructive-400 mb-3">
        {error}
      </p>
      <Button variant="outline" size="sm" onClick={onRetry}>
        <RefreshCw className="h-4 w-4 mr-2" />
        Retry
      </Button>
    </div>
  );
}

// ============================================
// ChatSessionSidebar Component
// ============================================

export function ChatSessionSidebar({ isCollapsed, onSessionSelect }: ChatSessionSidebarProps) {
  const {
    sessions,
    sessionsLoading,
    sessionsError,
    activeSessionId,
    loadSessions,
    createSession,
    selectSession,
    renameSession,
    deleteSession,
  } = useChatStore();

  const timeZone = useSettingsStore((state) => state.contextTimezone);
  const [teamOwnerFilter, setTeamOwnerFilter] = useState('all');
  const [teamCollapse, setTeamCollapse] = useState<TeamChatCollapseState>(
    () => readTeamChatCollapseState(globalThis.localStorage)
  );
  const myChats = useMemo(() => ownChats(sessions), [sessions]);
  const teamOwners = useMemo(
    () => groupTeamChats(sessions).map((group) => ({ key: group.key, ownerUsername: group.ownerUsername })),
    [sessions]
  );
  const teamGroups = useMemo(() => groupTeamChats(sessions, teamOwnerFilter), [sessions, teamOwnerFilter]);
  const updateTeamCollapse = useCallback((update: (state: TeamChatCollapseState) => TeamChatCollapseState) => {
    setTeamCollapse((state) => {
      const next = update(state);
      writeTeamChatCollapseState(globalThis.localStorage, next);
      return next;
    });
  }, []);

  const [creatingProvider, setCreatingProvider] = useState<'claude' | 'codex' | null>(null);
  const [codexConnected, setCodexConnected] = useState(false);
  const [showProviderMenu, setShowProviderMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const isCreating = creatingProvider !== null;

  // Load sessions on mount
  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  useEffect(() => {
    if (!showProviderMenu) return;

    function handlePointerDown(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowProviderMenu(false);
      }
    }

    function handleKeyDown(event: globalThis.KeyboardEvent) {
      if (event.key === 'Escape') {
        setShowProviderMenu(false);
      }
    }

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showProviderMenu]);

  useEffect(() => {
    let cancelled = false;

    async function loadCodexStatus() {
      try {
        const status = await api.codex.getStatus();
        if (!cancelled) {
          setCodexConnected(status.cliInstalled && status.authenticated);
        }
      } catch {
        if (!cancelled) {
          setCodexConnected(false);
        }
      }
    }

    void loadCodexStatus();
    window.addEventListener('codex-auth-changed', loadCodexStatus);

    return () => {
      cancelled = true;
      window.removeEventListener('codex-auth-changed', loadCodexStatus);
    };
  }, []);

  // Handle creating a new session
  const handleCreateSession = useCallback(async (provider: 'claude' | 'codex' = 'claude') => {
    setShowProviderMenu(false);
    setCreatingProvider(provider);
    try {
      const session = await createSession(undefined, provider, timeZone);
      if (session) {
        await selectSession(session.id);
        if (onSessionSelect) {
          onSessionSelect(session.id);
        }
      }
    } finally {
      setCreatingProvider(null);
    }
  }, [createSession, selectSession, onSessionSelect, timeZone]);

  // Handle selecting a session
  const handleSelectSession = useCallback(
    async (sessionId: string) => {
      await selectSession(sessionId);
      if (onSessionSelect) {
        onSessionSelect(sessionId);
      }
    },
    [selectSession, onSessionSelect]
  );

  // Handle renaming a session
  const handleRenameSession = useCallback(
    async (sessionId: string, name: string) => {
      return await renameSession(sessionId, name);
    },
    [renameSession]
  );

  // Deleting a session: the row menu requests it, the dialog confirms it.
  const [deleteState, dispatchDelete] = useReducer(chatDeleteReducer, INITIAL_CHAT_DELETE_STATE);
  const handleRequestDelete = useCallback((session: ChatSession) => {
    dispatchDelete({ type: 'request', sessionId: session.id, sessionName: session.name });
  }, []);
  const handleConfirmDelete = useCallback(async () => {
    if (deleteState.phase !== 'confirming') return;
    dispatchDelete({ type: 'confirm' });
    const deleted = await deleteSession(deleteState.sessionId);
    if (deleted) {
      dispatchDelete({ type: 'succeeded' });
    } else {
      dispatchDelete({
        type: 'failed',
        error: useChatStore.getState().sessionsError ?? 'The chat was not deleted.',
      });
    }
  }, [deleteState, deleteSession]);
  const deleteDialog = (
    <ConfirmDialog
      isOpen={deleteState.phase !== 'idle'}
      onClose={() => dispatchDelete({ type: 'cancel' })}
      onConfirm={() => void handleConfirmDelete()}
      title="Delete chat"
      message={deleteState.phase === 'idle' ? '' : chatDeleteConfirmationMessage(deleteState.sessionName)}
      confirmLabel="Delete chat"
      variant="destructive"
      isLoading={deleteState.phase === 'deleting'}
    />
  );

  // Collapsed view (icon only)
  if (isCollapsed) {
    return (
      <div className="flex flex-col gap-2 p-2">
        <Button
          variant="ghost"
          size="icon"
          className="w-full"
          onClick={() => handleCreateSession('claude')}
          disabled={isCreating}
          title="New chat"
        >
          {creatingProvider === 'claude' ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Plus className="h-4 w-4" />
          )}
        </Button>
      </div>
    );
  }

  // Loading state
  if (sessionsLoading && sessions.length === 0) {
    return (
      <div className="h-full flex flex-col">
        <div className="p-3 border-b border-surface-200 dark:border-surface-800">
          <Button variant="outline" size="sm" className="w-full" disabled>
            <Plus className="h-4 w-4 mr-2" />
            New Chat
          </Button>
        </div>
        <LoadingState />
      </div>
    );
  }

  // Error state
  if (sessionsError && sessions.length === 0) {
    return (
      <div className="h-full flex flex-col">
        <div className="p-3 border-b border-surface-200 dark:border-surface-800">
          <Button variant="outline" size="sm" className="w-full" disabled>
            <Plus className="h-4 w-4 mr-2" />
            New Chat
          </Button>
        </div>
        <ErrorState error={sessionsError} onRetry={loadSessions} />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header with New Chat button */}
      <div className="p-3 border-b border-surface-200 dark:border-surface-800">
        <div className="relative" ref={menuRef}>
          <Button
            variant="outline"
            size="sm"
            className="w-full justify-between"
            onClick={() => setShowProviderMenu((open) => !open)}
            disabled={isCreating}
          >
            <span className="flex items-center">
              {isCreating ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Plus className="h-4 w-4 mr-2" />
              )}
              {creatingProvider === 'claude'
                ? 'Creating Claude...'
                : creatingProvider === 'codex'
                ? 'Creating Codex...'
                : 'New Chat'}
            </span>
            <ChevronDown className="h-4 w-4 text-surface-500" />
          </Button>

          {showProviderMenu && (
            <div className="absolute left-0 right-0 top-full z-20 mt-2 overflow-hidden rounded-md border border-surface-200 bg-white shadow-lg dark:border-surface-700 dark:bg-surface-900">
              <button
                type="button"
                className="w-full px-3 py-2 text-left text-sm text-surface-700 hover:bg-surface-100 dark:text-surface-200 dark:hover:bg-surface-800"
                onClick={() => handleCreateSession('claude')}
              >
                Claude
              </button>
              <button
                type="button"
                className={cn(
                  'w-full px-3 py-2 text-left text-sm',
                  codexConnected
                    ? 'text-surface-700 hover:bg-surface-100 dark:text-surface-200 dark:hover:bg-surface-800'
                    : 'cursor-not-allowed text-surface-400 dark:text-surface-600'
                )}
                onClick={() => {
                  if (codexConnected) {
                    handleCreateSession('codex');
                  }
                }}
                title={codexConnected ? 'Start a Codex chat' : 'Connect Codex in Settings first'}
              >
                Codex
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Session list */}
      <div className="flex-1 overflow-y-auto py-2">
        {deleteState.error && (
          <ChatDeleteError error={deleteState.error} onDismiss={() => dispatchDelete({ type: 'dismissError' })} />
        )}
        <h3 className="px-3 py-1 text-xs font-semibold uppercase tracking-wide text-surface-500 dark:text-surface-400">
          My chats
        </h3>
        {myChats.map((session) => (
          <SessionItem
            key={session.id}
            session={session}
            isActive={session.id === activeSessionId}
            onSelect={() => handleSelectSession(session.id)}
            onRename={(name) => handleRenameSession(session.id, name)}
            onRequestDelete={handleRequestDelete}
          />
        ))}
        <TeamChatsSection
          groups={teamGroups}
          owners={teamOwners}
          ownerFilter={teamOwnerFilter}
          collapse={teamCollapse}
          activeSessionId={activeSessionId}
          timeZone={timeZone}
          onOwnerFilterChange={setTeamOwnerFilter}
          onToggleTeam={() => updateTeamCollapse((state) => ({ ...state, teamCollapsed: !state.teamCollapsed }))}
          onToggleGroup={(groupKey) => updateTeamCollapse((state) => toggleCollapsedGroup(state, groupKey))}
          onSelect={(sessionId) => void handleSelectSession(sessionId)}
          renderDelete={(session) => <ChatRowActions session={session} onRequestDelete={handleRequestDelete} />}
        />
      </div>
      {deleteDialog}

      {/* Loading indicator during refresh */}
      {sessionsLoading && sessions.length > 0 && (
        <div className="p-2 border-t border-surface-200 dark:border-surface-800">
          <div className="flex items-center justify-center gap-2 text-xs text-surface-500">
            <Loader2 className="h-3 w-3 animate-spin" />
            Refreshing...
          </div>
        </div>
      )}
    </div>
  );
}

export default ChatSessionSidebar;
